<script lang="ts">
import { computed, defineComponent, inject, onMounted, onUnmounted, ref, watch } from "vue";
import adressUtils from "@/businesslogic/utils/adressUtils";
import formValidierung from "@/businesslogic/formValidierung";
import AnfrageService from "@/businesslogic/services/AnfrageService";
import EmailBestaetigung from "@/components/vorschlag/modals/EmailBestaetigung.vue";
import Fehlermeldung from "@/views/phv/modals/Fehlermeldung.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import IconSprite from "@/components/IconSprite.vue";
import Logger from "js-logger";
import ferienhausAnfrageService from "@/businesslogic/services/FerienhausAnfrageService";
import BuendelDokumentLinks from "@/components/vorschlag/modals/BuendelDokumentLinks.vue";
import Modal from "@/components/Modal.vue";
import dateUtils from "@/businesslogic/utils/dateUtils";
import storeUtils from "@/businesslogic/utils/storeUtils";
import { Status } from "@/model/Anfrage";
import Anrede from "@/components/Anrede.vue";
import anredeUtils from "@/businesslogic/utils/anredeUtils";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "VorschlagMakler",
  components: {
    Zelle,
    TitelZelle,
    Zeile,
    Anrede,
    ValidierungsFehler,
    EmailBestaetigung,
    Fehlermeldung,
    IconSprite,
    Modal,
    BuendelDokumentLinks,
  },
  emits: ["closeModal", "textVerbergen"],
  props: {
    produkt: String,
    isVorschlagEmail: Boolean,
    antrag: {
      type: Boolean,
      default: false,
    },
    trackingZusatz: String,
  },
  setup(props, { emit }) {
    const istSichtbar: any = inject("istSichtbar");
    const piniaStore = useFormStore();
    const zeigeSpinner: any = inject("zeigeSpinner");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const fehler = ref({} as any);
    const { getIsoDateNow, getIsoDatePlusYears } = dateUtils();
    const { equals: adressEquals, empty: adressEmpty } = adressUtils();
    const { istFhkVerkaufsprodukt, istVhvVerkaufsprodukt, enthaeltVGV, enthaeltVHV } = storeUtils();
    const { anredeIstOrganisation, anredeIstEtgOderWeg, anredeIstFirma } = anredeUtils();
    const showError = ref(false);
    const showSuccess = ref(false);

    const speichernMoeglich = computed(() => {
      return !piniaStore.state.isMaklerLink;
    });

    function eingabenSindValide() {
      return new Promise((resolve) => {
        const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();
        validiereForm("vorschlagMakler").then(() => {
          fehler.value = getFormFehler();
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    }

    function closeModal() {
      emit("closeModal");
    }

    function vorschlagPerMail() {
      // Siehe https://atlas.grundvers.de/jira/browse/TARGET-2913
      // Es gibt momentan kein Feld fürs Annehmen von Datenschutzhinweisen in der Maklerstrecke.
      // Falls die Quote-Reminder-Funktionalität irgendwann für die Maklerstrecken aktiviert werden soll,
      // kann man z.B. hier den Flag setzen wie folgt:
      // piniaStore.state.datenschutzhinweiseAkzeptiert = true;
      AnfrageService.versenden(
        piniaStore.getSessionToken(piniaStore.state),
        piniaStore.state,
        props.antrag,
        "VorschlagMakler",
      )
        .then((res) => {
          Logger.debug("vorschlagPerMail(): versenden erfolgreich. AnfrageID: " + res.data.id);
          piniaStore.state.id = res.data.id;
          piniaStore.state.status = res.data.status;
          piniaStore.state.erstelldatum = res.data.erstelldatum;
          showSuccess.value = true;
          piniaStore.state.adresseFelderSchrittDreiDeaktivieren = true;
          emit("textVerbergen");
        })
        .catch((err) => {
          Logger.debug("FEHLER in vorschlagPerMail():", err);
          showError.value = true;
          zeigeSpinner.value = false;
          emit("textVerbergen");
        })
        .then(() => {
          //wird immer ausgeführt...
          zeigeSpinner.value = false;
        });
    }

    async function versendeBuendelEmail() {
      const error = new Array<any>();
      const eingeschlosseneProdukte = ferienhausAnfrageService.getFHKProdukte(piniaStore.state);

      for await (const produktId of eingeschlosseneProdukte) {
        await ferienhausAnfrageService
          .versenden(
            piniaStore.getSessionToken(piniaStore.state),
            piniaStore.state,
            props.antrag,
            "VorschlagMakler",
            produktId,
          )
          .then((res) => {
            Logger.debug("vorschlagPerMail(): versenden erfolgreich. AnfrageID: " + res.data.id);
            piniaStore.state.id = res.data.id;
            piniaStore.state.status = res.data.status;
            piniaStore.state.erstelldatum = res.data.erstelldatum;
          })
          .catch((err) => {
            Logger.debug("ProduktId: ", produktId, " FEHLER in submitModalData():", err);
            error.push(err);
          });
      }

      if (error.length > 0) {
        showError.value = true;
      } else {
        showSuccess.value = true;
        piniaStore.state.adresseFelderSchrittDreiDeaktivieren = true;
      }

      zeigeSpinner.value = false;
      emit("textVerbergen");
    }

    function vorschlagHerunterladen() {
      AnfrageService.herunterladen(
        piniaStore.getSessionToken(piniaStore.state),
        piniaStore.state,
        props.antrag,
        "VorschlagMakler",
        props.trackingZusatz,
      )
        .then((res) => {
          Logger.debug("vorschlagHerunterladen(): versenden erfolgreich. AnfrageID: " + res.data.id);
          piniaStore.state.id = res.data.id;
          piniaStore.state.status = res.data.status;
          piniaStore.state.erstelldatum = res.data.erstelldatum;
          Logger.debug("Response", res.data);
          const fileLink = document.createElement("a");
          fileLink.href = "data:application/pdf;base64," + res.data.korrespondenz.vorschlagDokument;
          fileLink.setAttribute("download", res.data.korrespondenz.dokumentName);
          document.body.appendChild(fileLink);
          fileLink.click();
          piniaStore.state.adresseFelderSchrittDreiDeaktivieren = true;
          emit("closeModal");
        })
        .catch((err) => {
          Logger.debug("FEHLER in vorschlagHerunterladen():", err);
          showError.value = true;
          emit("textVerbergen");
        })
        .then(() => {
          //wird immer ausgeführt...
          zeigeSpinner.value = false;
        });
    }

    const dokumentNeuSpeichern = () => {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          piniaStore.state.id = "";
          piniaStore.state.anfrageNummer = 0;
          piniaStore.state.status = Status.NEU;
          piniaStore.state.subStatus = "";
          piniaStore.state.erstelldatum = "";

          dokumentSpeichern();
        }
      });
    };

    function dokumentSpeichern() {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          zeigeSpinner.value = true;
          if (istFhkVerkaufsprodukt(piniaStore.state)) {
            buendelSpeichern();
          } else {
            speichern();
          }
        } else {
          const elem = document.querySelector("div#tariffOptionsTable");
          if (elem) {
            const rect = elem.getBoundingClientRect(),
              scrollTop = window.scrollY || document.documentElement.scrollTop;
            window.scrollTo(0, rect.top + scrollTop);
          }
        }
      });
    }

    function speichern() {
      AnfrageService.speichern(
        piniaStore.getSessionToken(piniaStore.state),
        piniaStore.state,
        props.antrag,
        "VorschlagMakler",
      )
        .then((res) => {
          Logger.debug("dokumentSpeichern(): speichern erfolgreich. AnfrageID: " + res.data.id);

          piniaStore.state.id = res.data.id;
          piniaStore.state.status = res.data.status;
          piniaStore.state.erstelldatum = res.data.erstelldatum;
          Logger.debug("Response", res.data);
          emit("closeModal");
        })
        .catch((err) => {
          Logger.debug("FEHLER in dokumentSpeichern():", err);
          showError.value = true;
          zeigeSpinner.value = false;
          emit("textVerbergen");
        })
        .then(() => {
          //wird immer ausgeführt...
          zeigeSpinner.value = false;
        });
    }

    async function buendelSpeichern() {
      const error = new Array<any>();
      const eingeschlosseneProdukte = ferienhausAnfrageService.getFHKProdukte(piniaStore.state);

      for await (const produktId of eingeschlosseneProdukte) {
        await ferienhausAnfrageService
          .speichern(
            piniaStore.getSessionToken(piniaStore.state),
            piniaStore.state,
            props.antrag,
            "VorschlagMakler",
            produktId,
          )
          .then((res) => {
            Logger.debug("vorschlagPerMail(): versenden erfolgreich. AnfrageID: " + res.data.id);
            piniaStore.state.id = res.data.id;
            piniaStore.state.status = res.data.status;
            piniaStore.state.erstelldatum = res.data.erstelldatum;
            Logger.debug("Response", res.data);
          })
          .catch((err) => {
            Logger.debug("FEHLER in dokumentSpeichern():", err);
            error.push(err);
          });
      }

      if (error.length > 0) {
        showError.value = true;
        emit("textVerbergen");
      } else {
        showSuccess.value = true;
        piniaStore.state.adresseFelderSchrittDreiDeaktivieren = true;
        emit("closeModal");
      }

      zeigeSpinner.value = false;
    }

    function submitModalData() {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          if (props.isVorschlagEmail) {
            zeigeSpinner.value = true;
            if (istFhkVerkaufsprodukt(piniaStore.state)) {
              versendeBuendelEmail();
            } else {
              vorschlagPerMail();
            }
          } else {
            if (istFhkVerkaufsprodukt(piniaStore.state)) {
              wechsleSichtbarkeit("modal-ferienhausDownload");
            } else {
              zeigeSpinner.value = true;
              vorschlagHerunterladen();
            }
          }
        } else {
          const elem = document.querySelector("div#tariffOptionsTable");
          if (elem) {
            const rect = elem.getBoundingClientRect(),
              scrollTop = window.scrollY || document.documentElement.scrollTop;
            window.scrollTo(0, rect.top + scrollTop);
          }
        }
      });
    }

    function resetForm() {
      showError.value = false;
      showSuccess.value = false;
    }

    const isOrganisation = computed(() => {
      return anredeIstOrganisation(piniaStore.state.personendaten.versicherungsnehmer.anrede);
    });

    const labelOrganisation = computed(() => {
      switch (piniaStore.state.personendaten.versicherungsnehmer.anrede) {
        case "0003":
          return "Firma";
        case "ETG":
          return "EG";
        case "WEG":
          return "WEG";
        default:
          return "";
      }
    });

    const isEtgWeg = computed(() => {
      return anredeIstEtgOderWeg(piniaStore.state.personendaten.versicherungsnehmer.anrede);
    });

    watch(
      () => piniaStore.state.personendaten.versicherungsnehmer.anrede,
      async (neueAnrede) => {
        if (anredeIstEtgOderWeg(neueAnrede)) {
          piniaStore.state.personendaten.verwalterAktiv = true;
        }
      },
    );

    const istFirma = computed(() => {
      return anredeIstFirma(piniaStore.state.personendaten.versicherungsnehmer.anrede);
    });

    const istVerwalterFirma = computed(() => {
      return anredeIstFirma(piniaStore.state.personendaten.verwalter.anrede);
    });

    const organisationPlatzhalter = computed(() => {
      if (labelOrganisation.value !== "") {
        return "Name der " + labelOrganisation.value;
      }
      return "";
    });

    const organisationFehlermeldung = computed(() => {
      return "Bitte geben Sie den Namen der " + labelOrganisation.value + " ein (2-40 Zeichen).";
    });

    const beschriftung = computed(() => {
      return props.antrag ? "Antrag" : "Vorschlag";
    });

    onMounted(() => {
      if (
        piniaStore.state.verkaufsprodukt === "VGV" &&
        adressEmpty(piniaStore.state.personendaten.versicherungsnehmer.adresse)
      ) {
        piniaStore.state.personendaten.versicherungsnehmer.adresse.plz = piniaStore.state.risikoAnschrift.plz;
        piniaStore.state.personendaten.versicherungsnehmer.adresse.ort = piniaStore.state.risikoAnschrift.ort;
        piniaStore.state.personendaten.versicherungsnehmer.adresse.strasse = piniaStore.state.risikoAnschrift.strasse;
        piniaStore.state.personendaten.versicherungsnehmer.adresse.hausnummer =
          piniaStore.state.risikoAnschrift.hausnummer;
        piniaStore.state.personendaten.versicherungsnehmer.adresseAbweichendVonRisikoadresse = false;
      }
      if (props.antrag && piniaStore.state.korrespondenz.beilagen === "NUR_VORSCHLAG") {
        piniaStore.state.korrespondenz.beilagen = "VORSCHLAG_UND_PRODUKTINFORMATIONEN";
      }
    });

    onUnmounted(() => {
      if (piniaStore.state.verkaufsprodukt === "VGV") {
        if (
          !adressEquals(piniaStore.state.risikoAnschrift, piniaStore.state.personendaten.versicherungsnehmer.adresse)
        ) {
          piniaStore.state.personendaten.versicherungsnehmer.adresseAbweichendVonRisikoadresse = true;
        } else {
          piniaStore.state.personendaten.versicherungsnehmer.adresseAbweichendVonRisikoadresse = false;
        }
      }
    });

    return {
      piniaStore,
      submitModalData,
      closeModal,
      fehler,
      getIsoDateNow,
      showError,
      showSuccess,
      resetForm,
      isEtgWeg,
      isOrganisation,
      labelOrganisation,
      organisationPlatzhalter,
      organisationFehlermeldung,
      istVerwalterFirma,
      istFirma,
      beschriftung,
      dokumentSpeichern,
      speichernMoeglich,
      wechsleSichtbarkeit,
      istSichtbar,
      dokumentNeuSpeichern,
      getIsoDatePlusYears,
      istVhvVerkaufsprodukt,
      istFhkVerkaufsprodukt,
      enthaeltVGV,
      enthaeltVHV,
    };
  },
});
</script>

<template>
  <form id="vorschlagMakler" novalidate v-if="!showError && !showSuccess">
    <!--################################################################################## -->
    <!--########################### Versicherungsnehmer ################################## -->
    <!--################################################################################## -->

    <!-- Versicherungsnehmer Anrede -->
    <zeile>
      <titel-zelle :vertikal-zentrieren="false" :ausrichtung-responsiv="false">Anrede</titel-zelle>
      <zelle :breite="12" class="u-form-grid-nospace-y">
        <Anrede
          id="anrede"
          :required="true"
          v-model:modelValue="piniaStore.state.personendaten.versicherungsnehmer.anrede"
          ariaLabel="Anrede"
          tabindex="0"
          :autofocus="true"
          :fehler="fehler"
        />
      </zelle>
    </zeile>

    <!-- Versicherungsnehmer Firma-, ETG-, WEG-Name -->
    <zeile v-if="isOrganisation">
      <titel-zelle :vertikal-zentrieren="false" :ausrichtung-responsiv="false"
        >Name der {{ labelOrganisation }}</titel-zelle
      >
      <zelle :breite="12" class="u-form-grid-nospace-y">
        <input
          type="text"
          id="organisation"
          name="organisation"
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.organisation"
          class="c-form-control__input"
          data-cy="organisation"
          :placeholder="organisationPlatzhalter"
          minlength="2"
          maxlength="40"
          required
          aria-required="true"
          :aria-label="labelOrganisation"
          :data-fehlermeldung="organisationFehlermeldung"
        />
        <validierungs-fehler :fehler-text="fehler.organisation" />
      </zelle>
    </zeile>

    <!-- Versicherungsnehmer Vorname/Name -->
    <zeile v-if="!isEtgWeg">
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Vorname {{ !istFirma ? "" : " (optional)" }}
      </titel-zelle>
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Name {{ !istFirma ? "" : " (optional)" }}
      </titel-zelle>
    </zeile>
    <zeile v-if="!isEtgWeg">
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          type="text"
          id="vorname"
          name="vorname"
          class="c-form-control__input"
          data-cy="vorname"
          placeholder="Vorname"
          minlength="2"
          maxlength="40"
          :required="!istFirma"
          :aria-required="!istFirma"
          aria-label="Vorname"
          data-fehlermeldung="Bitte den Vornamen eingeben (2-40 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.vorname"
        />
        <validierungs-fehler :fehler-text="fehler.vorname" />
      </zelle>
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          type="text"
          id="nachname"
          name="nachname"
          class="c-form-control__input"
          data-cy="nachname"
          placeholder="Name"
          minlength="2"
          maxlength="40"
          :required="!istFirma"
          :aria-required="!istFirma"
          aria-label="Nachname"
          data-parsley-combined-name-length="40"
          data-fehlermeldung="Bitte den Nachnamen eingeben (2-40 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.nachname"
        />
        <validierungs-fehler :fehler-text="fehler.nachname" />
      </zelle>
    </zeile>

    <!-- Versicherungsnehmer PLZ/Ort -->
    <zeile v-if="!isEtgWeg">
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        PLZ (optional)
      </titel-zelle>
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Ort (optional)
      </titel-zelle>
    </zeile>
    <zeile v-if="!isEtgWeg">
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          id="plz"
          name="plz"
          class="c-form-control__input"
          data-cy="plz"
          type="text"
          placeholder="PLZ"
          minlength="5"
          maxlength="5"
          min="01001"
          max="99998"
          aria-label="Postleitzahl"
          data-fehlermeldung="Bitte eine gültige
                Postleitzahl eingeben."
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.adresse.plz"
          :disabled="istVhvVerkaufsprodukt(piniaStore.state)"
        />
        <validierungs-fehler :fehler-text="fehler.plz" />
      </zelle>
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          id="ort"
          name="ort"
          class="c-form-control__input"
          data-cy="ort"
          type="text"
          placeholder="Ort"
          minlength="2"
          maxlength="40"
          aria-label="Ort"
          data-fehlermeldung="Bitte den Ort eingeben (max. 40 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.adresse.ort"
        />
        <validierungs-fehler :fehler-text="fehler.ort" />
      </zelle>
    </zeile>

    <!-- Versicherungsnehmer Strasse/Hausnummer -->
    <zeile v-if="!isEtgWeg">
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Straße (optional)
      </titel-zelle>
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Nummer (optional)
      </titel-zelle>
    </zeile>
    <zeile v-if="!isEtgWeg">
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          id="strasse"
          name="strasse"
          class="c-form-control__input"
          data-cy="strasse"
          type="text"
          placeholder="Straße"
          minlength="2"
          maxlength="60"
          aria-label="Straße"
          data-fehlermeldung="Bitte die Straße eingeben (max. 60 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.adresse.strasse"
        />
        <validierungs-fehler :fehler-text="fehler.strasse" />
      </zelle>
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          id="nummer"
          name="nummer"
          class="c-form-control__input"
          data-cy="hausnummer"
          type="text"
          placeholder="Nr."
          minlength="1"
          maxlength="10"
          aria-label="Hausnummer"
          data-fehlermeldung="Bitte die Hausnummer eingeben (max. 10 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.adresse.hausnummer"
        />
        <validierungs-fehler :fehler-text="fehler.nummer" />
      </zelle>
    </zeile>

    <!--################################################################################## -->
    <!--################################ Verwalter ####################################### -->
    <!--################################################################################## -->

    <!-- Verwalter Anrede/Titel -->
    <zeile v-if="isEtgWeg">
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Anrede des Verwalters
      </titel-zelle>
      <titel-zelle v-if="!istVerwalterFirma" :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Titel des Verwalters
      </titel-zelle>
    </zeile>
    <zeile v-if="isEtgWeg">
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <Anrede
          id="verwalterAnrede"
          :fehler="fehler"
          :ist-verwalter="true"
          v-model:modelValue="piniaStore.state.personendaten.verwalter.anrede"
          :required="true"
          ariaLabel="Anrede des Verwalters"
        />
      </zelle>
      <zelle v-if="!istVerwalterFirma" :breite="6">
        <select
          id="verwalterTitel"
          name="verwalterTitel"
          v-model="piniaStore.state.personendaten.verwalter.titel"
          class="c-form-control-select"
          data-cy="verwalterTitel"
          aria-label="Titel des Verwalters"
        >
          <option value="">Titel</option>
          <option value="Dipl.Ing.">Dipl. Ing.</option>
          <option value="Dr.">Dr.</option>
          <option value="Prof.Dr.">Prof. Dr.</option>
        </select>
        <validierungs-fehler :fehler-text="fehler.verwalterTitel" :reduzierter-abstand="true" />
      </zelle>
    </zeile>

    <!-- Verwalter Firma-Name  -->
    <zeile v-if="isEtgWeg && istVerwalterFirma">
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Name der Firma
      </titel-zelle>
      <zelle :breite="12" class="u-form-grid-nospace-y">
        <input
          type="text"
          id="verwalterOrganisation"
          name="verwalterOrganisation"
          v-model.lazy.trim="piniaStore.state.personendaten.verwalter.organisation"
          class="c-form-control__input"
          data-cy="verwalter-organisation"
          placeholder="Firma"
          minlength="2"
          maxlength="40"
          :required="istVerwalterFirma"
          :aria-required="istVerwalterFirma"
          aria-label="Name der Verwalterfirma"
          data-fehlermeldung="Bitte geben Sie den Namen der Firma ein (2-40 Zeichen)."
        />
        <validierungs-fehler :fehler-text="fehler.verwalterOrganisation" />
      </zelle>
    </zeile>

    <!-- Verwalter Vorname/Name  -->
    <zeile v-if="isEtgWeg">
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Vorname des Verwalters
        {{ !istVerwalterFirma ? "" : " (optional)" }}
      </titel-zelle>
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Name des Verwalters
        {{ !istVerwalterFirma ? "" : " (optional)" }}
      </titel-zelle>
    </zeile>
    <zeile v-if="isEtgWeg">
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          type="text"
          id="vornameVerwalter"
          name="vornameVerwalter"
          class="c-form-control__input"
          data-cy="vorname"
          placeholder="Vorname"
          minlength="2"
          maxlength="40"
          :required="!istVerwalterFirma"
          :aria-required="!istVerwalterFirma"
          aria-label="Vorname"
          data-fehlermeldung="Bitte den Vornamen eingeben (2-40 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.verwalter.vorname"
        />
        <validierungs-fehler :fehler-text="fehler.vornameVerwalter" />
      </zelle>
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          type="text"
          id="nachnameVerwalter"
          name="nachnameVerwalter"
          class="c-form-control__input"
          data-cy="nachname"
          placeholder="Name"
          minlength="2"
          maxlength="40"
          :required="!istVerwalterFirma"
          :aria-required="!istVerwalterFirma"
          aria-label="Nachname"
          data-parsley-combined-name-length="40"
          data-fehlermeldung="Bitte den Nachnamen eingeben (2-40 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.verwalter.nachname"
        />
        <validierungs-fehler :fehler-text="fehler.nachnameVerwalter" />
      </zelle>
    </zeile>

    <!-- Verwalter PLZ/Ort -->
    <zeile v-if="isEtgWeg">
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        PLZ (optional)
      </titel-zelle>
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Ort (optional)
      </titel-zelle>
    </zeile>
    <zeile v-if="isEtgWeg">
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          id="plzVerwalter"
          name="plzVerwalter"
          class="c-form-control__input"
          data-cy="plz"
          type="text"
          placeholder="PLZ"
          minlength="5"
          maxlength="5"
          min="01001"
          max="99998"
          aria-label="Postleitzahl"
          data-fehlermeldung="Bitte eine gültige Postleitzahl eingeben."
          v-model.lazy.trim="piniaStore.state.personendaten.verwalter.adresse.plz"
        />
        <validierungs-fehler :fehler-text="fehler.plzVerwalter" />
      </zelle>
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          id="ortVerwalter"
          name="ortVerwalter"
          class="c-form-control__input"
          data-cy="ort"
          type="text"
          placeholder="Ort"
          minlength="2"
          maxlength="40"
          aria-label="Ort"
          data-fehlermeldung="Bitte den Ort eingeben (max. 40 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.verwalter.adresse.ort"
        />
        <validierungs-fehler :fehler-text="fehler.ortVerwalter" />
      </zelle>
    </zeile>

    <!-- Verwalter Strasse/Hausnummer -->
    <zeile v-if="isEtgWeg">
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Straße (optional)
      </titel-zelle>
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Nummer (optional)
      </titel-zelle>
    </zeile>
    <zeile v-if="isEtgWeg">
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          id="strasseVerwalter"
          name="strasseVerwalter"
          class="c-form-control__input"
          data-cy="strasse"
          type="text"
          placeholder="Straße"
          minlength="2"
          maxlength="60"
          aria-label="Straße"
          data-fehlermeldung="Bitte die Straße eingeben (max. 60 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.verwalter.adresse.strasse"
        />
        <validierungs-fehler :fehler-text="fehler.strasseVerwalter" />
      </zelle>
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          id="nummerVerwalter"
          name="nummerVerwalter"
          class="c-form-control__input"
          data-cy="hausnummer"
          type="text"
          placeholder="Nr."
          minlength="1"
          maxlength="10"
          aria-label="Hausnummer"
          data-fehlermeldung="Bitte die Hausnummer eingeben (max. 10 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.verwalter.adresse.hausnummer"
        />
        <validierungs-fehler :fehler-text="fehler.nummerVerwalter" />
      </zelle>
    </zeile>

    <!--################################################################################## -->
    <!--############################# Korrespondenz ###################################### -->
    <!--################################################################################## -->

    <!-- Versicherungsbeginn -->
    <zeile v-if="!istFhkVerkaufsprodukt(piniaStore.state)">
      <titel-zelle :vertikal-zentrieren="false" :ausrichtung-responsiv="false">Versicherungsbeginn</titel-zelle>
      <zelle :breite="12" class="u-form-grid-nospace-y">
        <input
          id="versicherungsbeginn"
          name="versicherungsbeginn"
          class="c-form-control__input"
          type="date"
          data-cy="tariffStartDate"
          title="Versicherungsbeginn im Format TT.MM.JJJJ"
          v-bind:min="getIsoDateNow()"
          v-bind:max="getIsoDatePlusYears(2)"
          minlength="10"
          maxlength="10"
          pattern="[0-9]{2}.[0-9]{2}.[0-9]{4}"
          required
          aria-required="true"
          aria-label="Versicherungsbeginn im Format TT.MM.JJJJ"
          v-model.lazy.trim="piniaStore.state.tarifinformationen.versicherungsbeginn"
        />
        <validierungs-fehler :fehler-text="fehler.versicherungsbeginn" />
      </zelle>
    </zeile>

    <zeile v-if="istFhkVerkaufsprodukt(piniaStore.state) && enthaeltVGV(piniaStore.state)">
      <titel-zelle :breite="12" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        Versicherungsbeginn <strong>Wohngebäude</strong>
      </titel-zelle>
      <zelle :breite="12" class="u-form-grid-nospace-y">
        <input
          id="versicherungsbeginn"
          name="versicherungsbeginn"
          class="c-form-control__input"
          type="date"
          data-cy="tariffStartDate"
          title="Versicherungsbeginn im Format TT.MM.JJJJ"
          v-bind:min="getIsoDateNow()"
          v-bind:max="getIsoDatePlusYears(2)"
          minlength="10"
          maxlength="10"
          pattern="[0-9]{2}.[0-9]{2}.[0-9]{4}"
          required
          aria-required="true"
          aria-label="Versicherungsbeginn Wohngebäude im Format TT.MM.JJJJ"
          v-model.lazy.trim="piniaStore.state.ferienhaus.vgv.tarifinformationen.versicherungsbeginn"
        />
        <validierungs-fehler :fehler-text="fehler.versicherungsbeginn" />
      </zelle>
    </zeile>

    <zeile v-if="istFhkVerkaufsprodukt(piniaStore.state) && enthaeltVHV(piniaStore.state)">
      <titel-zelle :breite="12" :vertikal-zentrieren="false" :ausrichtung-responsiv="false"
        >Versicherungsbeginn <strong>Hausrat</strong></titel-zelle
      >
      <zelle :breite="12" class="u-form-grid-nospace-y">
        <input
          id="versicherungsbeginn"
          name="versicherungsbeginn"
          class="c-form-control__input"
          type="date"
          data-cy="tariffStartDate"
          title="Versicherungsbeginn im Format TT.MM.JJJJ"
          v-bind:min="getIsoDateNow()"
          v-bind:max="getIsoDatePlusYears(2)"
          minlength="10"
          maxlength="10"
          pattern="[0-9]{2}.[0-9]{2}.[0-9]{4}"
          required
          aria-required="true"
          aria-label="Versicherungsbeginn Hausrat im Format TT.MM.JJJJ"
          v-model.lazy.trim="piniaStore.state.ferienhaus.vhv.tarifinformationen.versicherungsbeginn"
        />
        <validierungs-fehler :fehler-text="fehler.versicherungsbeginn" />
      </zelle>
    </zeile>

    <zeile
      v-if="
        istFhkVerkaufsprodukt(piniaStore.state) &&
        enthaeltVGV(piniaStore.state) &&
        piniaStore.state.tarifinformationen.hausundgrundbesitzerhaftpflicht
      "
    >
      <titel-zelle :breite="12" :vertikal-zentrieren="false" :ausrichtung-responsiv="false"
        >Versicherungsbeginn <strong>Haus- und Grundbesitzerhaftpflicht</strong></titel-zelle
      >
      <zelle :breite="12" class="u-form-grid-nospace-y">
        <input
          id="versicherungsbeginn"
          name="versicherungsbeginn"
          class="c-form-control__input"
          type="date"
          data-cy="tariffStartDate"
          title="Versicherungsbeginn im Format TT.MM.JJJJ"
          v-bind:min="getIsoDateNow()"
          v-bind:max="getIsoDatePlusYears(2)"
          minlength="10"
          maxlength="10"
          pattern="[0-9]{2}.[0-9]{2}.[0-9]{4}"
          required
          aria-required="true"
          aria-label="Versicherungsbeginn Haus- und Grundbesitzerhaftpflicht im Format TT.MM.JJJJ"
          v-model.lazy.trim="piniaStore.state.ferienhaus.hug.tarifinformationen.versicherungsbeginn"
        />
        <validierungs-fehler :fehler-text="fehler.versicherungsbeginn" />
      </zelle>
    </zeile>

    <!-- Beilagen -->
    <zeile>
      <titel-zelle :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        <h3 class="u-color-primary">
          <strong>Beilagen</strong>
        </h3>
      </titel-zelle>
    </zeile>
    <zeile>
      <zelle v-if="!antrag" :breite="4" class="u-form-grid-nospace-y">
        <input
          id="nurVorschlag"
          name="nurVorschlag"
          class="c-form-control-radio__input"
          data-cy="nurVorschlag"
          type="radio"
          value="NUR_VORSCHLAG"
          v-model="piniaStore.state.korrespondenz.beilagen"
          aria-label="Nur Vorschlag"
        />
        <label for="nurVorschlag" class="c-form-control-radio__label">
          <span aria-hidden="true" class="c-form-control-radio__icon"></span>
          <span class="c-form-control-radio__label-text">Nur Vorschlag</span>
        </label>
      </zelle>
      <zelle :breite="4" class="u-form-grid-nospace-y">
        <input
          id="nurProduktInformationen"
          name="nurProduktinformationen"
          class="c-form-control-radio__input"
          data-cy="nurProduktInformationen"
          type="radio"
          value="VORSCHLAG_UND_PRODUKTINFORMATIONEN"
          v-model="piniaStore.state.korrespondenz.beilagen"
          aria-label="Antrag / Vorschlag mit Produktinformationen"
        />
        <label class="c-form-control-radio__label" for="nurProduktInformationen">
          <span aria-hidden="true" class="c-form-control-radio__icon"></span>
          <span class="c-form-control-radio__label-text">{{ antrag ? "Antrag" : "Vorschlag" }} mit Produktinfos</span>
        </label>
      </zelle>
      <zelle :breite="4" class="u-form-grid-nospace-y">
        <input
          id="vollstaendigeUnterlagen"
          name="vollstaendigeUnterlagen"
          class="c-form-control-radio__input"
          data-cy="vollstaendigeUnterlagen"
          type="radio"
          value="VOLLSTAENDIG"
          v-model="piniaStore.state.korrespondenz.beilagen"
          aria-label="Vollständige Unterlagen"
        />
        <label class="c-form-control-radio__label" for="vollstaendigeUnterlagen">
          <span aria-hidden="true" class="c-form-control-radio__icon"></span>
          <span class="c-form-control-radio__label-text">Vollständig</span>
        </label>
      </zelle>
    </zeile>

    <!-- Empfänger E-Mail -->
    <Zeile v-if="isVorschlagEmail">
      <titel-zelle :vertikal-zentrieren="false" :ausrichtung-responsiv="false">
        <h3 class="u-color-primary">
          <strong>Per E-Mail versenden an</strong>
        </h3></titel-zelle
      >
    </Zeile>
    <zeile v-if="isVorschlagEmail">
      <titel-zelle :vertikal-zentrieren="false" :ausrichtung-responsiv="false">E-Mail-Adresse Kunde</titel-zelle>
      <zelle v-if="!isEtgWeg" :breite="12" class="u-form-grid-nospace-y">
        <input
          type="email"
          id="email"
          name="email"
          class="c-form-control__input"
          data-cy="email"
          placeholder="E-Mail"
          minlength="2"
          maxlength="60"
          required
          aria-required="true"
          aria-label="Email"
          data-fehlermeldung="Bitte die E-Mail-Adresse eingeben (2-60 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.email"
        />
        <validierungs-fehler :fehler-text="fehler.email" />
      </zelle>
      <zelle v-if="isEtgWeg" :breite="12" class="u-form-grid-nospace-y">
        <input
          type="email"
          id="email"
          name="email"
          class="c-form-control__input"
          data-cy="email"
          placeholder="E-Mail"
          minlength="2"
          maxlength="60"
          required
          aria-required="true"
          aria-label="Email"
          data-fehlermeldung="Bitte die E-Mail-Adresse eingeben (2-60 Zeichen)."
          v-model.lazy.trim="piniaStore.state.personendaten.verwalter.email"
        />
        <validierungs-fehler :fehler-text="fehler.email" />
      </zelle>
    </zeile>

    <!-- Makler Kopie -->
    <zeile v-if="isVorschlagEmail" class="c-form-control-checkbox form-group">
      <zelle :breite="12">
        <input
          id="kopieAnMaklerCheck"
          name="kopieAnMaklerCheck"
          class="c-form-control-checkbox__input c-form-control-checkbox__input--spring"
          data-cy="kopieAnMaklerCheck"
          type="checkbox"
          data-fehlermeldung=""
          v-model="piniaStore.state.korrespondenz.kopieAnMakler"
          aria-label="Kopie an Makler-E-Mail-Adresse"
        />
        <label class="c-form-control-checkbox__label control-label" for="kopieAnMaklerCheck">
          <icon-sprite :ist-klein="true" css-klassen="c-form-control-checkbox__icon" icon-name="icon-check-mark" />
          <span class="c-form-control-checkbox__label-text">Kopie an Makler-E-Mail-Adresse.</span>
        </label>
        <validierungs-fehler :fehler-text="fehler.kopieAnMaklerCheck" />
      </zelle>
    </zeile>

    <!-- Buttons -->
    <zeile style="padding-top: 32px">
      <zelle :breite="3">
        <button
          id="quoteByEmailCancelBtn"
          class="c-button c-button--inverted"
          data-cy="cancelModalData"
          type="button"
          @click="closeModal()"
        >
          Schließen
        </button>
      </zelle>
      <zelle :breite="3">
        <button
          v-if="speichernMoeglich"
          id="quoteByEmailContinueBtn"
          class="c-button right"
          data-cy="submitModalData"
          type="button"
          @click="dokumentSpeichern()"
        >
          Speichern
        </button>
      </zelle>
      <zelle :breite="3">
        <button
          v-if="speichernMoeglich"
          id="quoteByEmailContinueBtn"
          class="c-button right u-pad-0 u-pad-x-1"
          data-cy="submitModalData"
          type="button"
          @click="dokumentNeuSpeichern()"
        >
          Variante speichern
        </button>
      </zelle>
      <zelle :breite="3">
        <button
          id="quoteByEmailContinueBtn"
          class="c-button right u-pad-0 u-pad-x-1"
          data-cy="submitModalData"
          type="button"
          @click="submitModalData()"
        >
          {{ isVorschlagEmail === true ? "E-Mail versenden" : "Herunterladen" }}
        </button>
      </zelle>
    </zeile>
  </form>

  <Fehlermeldung v-if="showError" @closeModal="closeModal" @resetForm="resetForm" />

  <EmailBestaetigung v-if="showSuccess" @closeModal="closeModal" />

  <!-- Ferienhaus -->
  <modal
    v-if="istSichtbar('modal-ferienhausDownload')"
    :titel="beschriftung + ' herunterladen'"
    text="Download-Links:"
    @closeModal="wechsleSichtbarkeit('modal-ferienhausDownload')"
  >
    <template #inhalt="inhaltProps">
      <buendel-dokument-links
        :antrag="antrag"
        :trackingZusatz="trackingZusatz"
        @textVerbergen="inhaltProps.textVerbergenToggle"
      />
    </template>
  </modal>
</template>
